import { zodResolver } from '@hookform/resolvers/zod';
import { useIonRouter } from '@ionic/react';
import {
  Permission,
  type ProfileModuleDto,
  type ProfileTemplate,
  type ProfileTemplateFieldMode,
  type ProfileTemplateFieldPoliciesDto,
  ProfileTemplatePublishStatus,
  ProfileTemplatesApi,
} from '@up2stay/up2promo-api-client';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useForm, useWatch } from 'react-hook-form';
import { useLocation, useParams } from 'react-router-dom';
import { toast } from 'sonner';

import type { ProfileTemplateDetailOverviewDict } from '@/core/components/profile-templates/profile-template-detail-overview';
import { buildTemplateDetailFixedLeadFields } from '@/core/components/profile-templates/template-detail-fixed-lead-fields';
import {
  executeTemplateDetailSave,
  prepareTemplateDetailSave,
} from '@/core/components/profile-templates/flush-template-detail-save';
import { templateDetailFormSchema } from '@/core/components/profile-templates/template-detail-form-schema';
import { buildTemplateDetailPreviewProfileData } from '@/core/components/profile-templates/template-detail-preview-data';
import type { TemplateMemberValuesDict } from '@/core/components/profile-templates/template-member-value-inputs';
import { useAutosaveCue } from '@/core/hooks/use-autosave-cue';
import { useEntitlements } from '@/core/hooks/use-entitlements';
import { useLocations } from '@/core/hooks/use-locations';
import { useNavigationLeaveGuard } from '@/core/hooks/use-navigation-leave-guard';
import { usePermissions } from '@/core/hooks/use-permissions';
import { useTemplateMemberRoster } from '@/core/hooks/use-template-member-roster';
import { useWorkspaceHasTeam } from '@/core/hooks/use-workspace-has-team';
import {
  goBackOrFallback,
  useIonBackRouter,
} from '@/core/lib/ion-back-navigation';
import { LIST_PAGE_HARD_CAP } from '@/core/lib/list-pagination';
import {
  ensureProfileModuleEntryIds,
  setEntryPolicyOnModule,
} from '@/core/lib/profile-module-entry-policies';
import { parseTemplateContent } from '@/core/lib/profile-template-content';
import {
  buildTemplateEditorSnapshot,
  extractApiErrorMessage,
  normalizeTemplateFormForSnapshot,
  sanitizeModulesForTemplateApi,
} from '@/core/lib/profile-template-editor-snapshot';
import {
  normalizeFieldPolicies,
  parseFieldPolicies,
  type ProfileTemplateFieldKey,
} from '@/core/lib/profile-template-fields';
import {
  applyTemplateBrandingEntitlement,
  contentToFormValues,
  formToTemplateContent,
  TEMPLATE_FORM_FIELD_NAMES,
  type TemplateFormData,
} from '@/core/lib/profile-template-form';
import { emitTemplateSyncStarted } from '@/core/lib/profile-template-sync-events';
import { useDictionary } from '@/core/providers/dictionary-provider';
import { shellPaths } from '@/core/router/app-paths';
import { useCurrentWorkspace } from '@/core/store/workspace.store';
import { useTemplateDetailLifecycle } from '@/core/components/profile-templates/use-template-detail-lifecycle';

export function useTemplateDetailEditor() {
  const dict = useDictionary();
  const pt = dict.workspace.profileTemplates;
  const pe = dict.profiles.edit;
  const ionRouter = useIonRouter();
  const backRouter = useIonBackRouter();
  const goBack = (fallback: string) => goBackOrFallback(backRouter, fallback);
  const redirect = useCallback(
    (path: string) => {
      ionRouter.push(path, 'root', 'replace');
    },
    [ionRouter],
  );
  const location = useLocation();
  const { templateId } = useParams<{ templateId: string }>();
  const workspace = useCurrentWorkspace();
  const { can } = usePermissions();
  const canManage = can(Permission.PROFILES_MANAGE_TEMPLATES);
  // DEV-507: hidden branding / custom accent need `feature.profile_branding`.
  // While entitlements load, keep the stored values (no premature rewrite).
  const { isFeatureEnabled, isLoading: entitlementsLoading } =
    useEntitlements();
  const canUseProfileBranding =
    entitlementsLoading || isFeatureEnabled('feature.profile_branding');
  const { hasTeam: workspaceHasTeam } = useWorkspaceHasTeam(workspace?.id);
  const { locations } = useLocations(undefined, {
    limit: LIST_PAGE_HARD_CAP,
    queryKeySuffix: 'template-preview',
  });

  const [loading, setLoading] = useState(true);
  const [isEditorReady, setIsEditorReady] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);
  const {
    status: autosaveStatus,
    markSaving,
    markSaved,
    markIdle,
  } = useAutosaveCue();
  const [name, setName] = useState('');
  // Last non-empty name: header / delete dialog fall back to it while the
  // field is cleared (an empty name is never saved, DEV-538).
  const [savedName, setSavedName] = useState('');
  const [description, setDescription] = useState('');
  const [policies, setPolicies] = useState<ProfileTemplateFieldPoliciesDto>({});
  const [modules, setModules] = useState<ProfileModuleDto[]>([]);
  const [templateTeamIds, setTemplateTeamIds] = useState<string[]>([]);
  const [publishStatus, setPublishStatus] =
    useState<ProfileTemplatePublishStatus>(ProfileTemplatePublishStatus.DRAFT);
  const [publishing, setPublishing] = useState(false);
  const [discardingChanges, setDiscardingChanges] = useState(false);
  const [liveRevision, setLiveRevision] = useState<number | null>(null);
  const [revisionHistoryKey, setRevisionHistoryKey] = useState(0);
  const [activeTab, setActiveTab] = useState('basic');
  const [showPreview, setShowPreview] = useState(false);
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);

  const { roster: memberRoster, loading: memberRosterLoading } =
    useTemplateMemberRoster(workspace?.id, templateTeamIds);

  const memberValuesDict = useMemo(
    (): TemplateMemberValuesDict => ({
      noTeams: pt.memberValues.noTeams,
      noMembers: pt.memberValues.noMembers,
      perMemberHint: pt.memberValues.perMemberHint,
      defaultForAll: pt.memberValues.defaultForAll,
      usesDefaultLabel: pt.memberValues.usesDefaultLabel,
      usesDefaultPlaceholder: pt.memberValues.usesDefaultPlaceholder,
      searchPlaceholder: pt.memberValues.searchPlaceholder,
      memberCount: pt.memberValues.memberCount,
      filterAll: pt.memberValues.filterAll,
      filterOverrides: pt.memberValues.filterOverrides,
      filterNoOverrides: pt.memberValues.filterNoOverrides,
      bulkEditHint: pt.memberValues.bulkEditHint,
      overrideCountLabel: pt.memberValues.overrideCountLabel,
      pendingChangesCount: pt.memberValues.pendingChangesCount,
      noSearchResults: pt.memberValues.noSearchResults,
      editButton: pt.memberValues.editButton,
      editButtonTitle: pt.memberValues.editButtonTitle,
      enabledLabel: pt.memberValues.enabledLabel,
      resetToDefault: pt.memberValues.resetToDefault,
      tableColumnMember: pt.memberValues.tableColumnMember,
      tableColumnValue: pt.memberValues.tableColumnValue,
      tableCancel: pt.memberValues.tableCancel,
      tableDone: pt.memberValues.tableDone,
      exportCsv: pt.memberValues.exportCsv,
      importCsv: pt.memberValues.importCsv,
      importSuccess: pt.memberValues.importSuccess,
      importError: pt.memberValues.importError,
      loadFailed: pt.memberValues.loadFailed,
      draftHint: pt.memberValues.draftHint,
      syncingHint: pt.memberValues.syncingHint,
    }),
    [pt.memberValues],
  );

  const saveTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const lastSyncedTemplateIdRef = useRef<string | null>(null);
  const previousSnapshotRef = useRef<string | null>(null);
  /** Disarmed until first post-hydrate observe absorbs normalize drift without PATCH. */
  const autosaveArmedRef = useRef(false);
  const pendingFormPatchRef = useRef<Partial<TemplateFormData>>({});
  const suppressNotFoundRef = useRef(false);
  const pathnameRef = useRef(location.pathname);
  const applyTemplateToFormRef = useRef<(t: ProfileTemplate) => void>(() => {});

  useEffect(() => {
    pathnameRef.current = location.pathname;
  }, [location.pathname]);

  const policyLabels = useMemo(
    () => ({
      locked: pt.modes.locked,
      free: pt.modes.free,
    }),
    [pt.modes.free, pt.modes.locked],
  );

  const fixedLeadFormFields = useMemo(() => {
    const fieldLabels = (
      pe.leadForm as { fieldLabels?: Record<string, string> }
    ).fieldLabels;
    return buildTemplateDetailFixedLeadFields(fieldLabels);
  }, [pe.leadForm]);

  const {
    register,
    control,
    formState: { errors },
    setValue,
    reset,
  } = useForm<TemplateFormData>({
    resolver: zodResolver(templateDetailFormSchema as never),
    defaultValues: contentToFormValues({ modules: [] }),
    shouldUnregister: false,
  });

  const watchedLanguage = useWatch({ control, name: 'language' });
  const watchedVisibility = useWatch({ control, name: 'visibility' });
  const watchedJobTitle = useWatch({ control, name: 'jobTitle' });
  const watchedCompany = useWatch({ control, name: 'company' });
  const watchedLocation = useWatch({ control, name: 'location' });
  const watchedShowProfileImage = useWatch({
    control,
    name: 'showProfileImage',
  });
  const watchedShowUp2promoBranding = useWatch({
    control,
    name: 'showUp2promoBranding',
  });
  const watchedAccentColor = useWatch({ control, name: 'accentColor' });
  const watchedLayout = useWatch({ control, name: 'layout' });
  const watchedTheme = useWatch({ control, name: 'theme' });
  const watchedHeaderStyle = useWatch({ control, name: 'headerStyle' });
  const watchedHeaderImage = useWatch({ control, name: 'headerImage' });
  const watchedButtonShape = useWatch({ control, name: 'buttonShape' });
  const watchedLeadFormEnabled = useWatch({ control, name: 'leadFormEnabled' });
  const watchedLeadFormVisibilityMode = useWatch({
    control,
    name: 'leadFormVisibilityMode',
  });
  const watchedLeadFormAutoOpenDelay = useWatch({
    control,
    name: 'leadFormAutoOpenDelay',
  });
  const watchedLeadsAutoShareWithWorkspace = useWatch({
    control,
    name: 'leadsAutoShareWithWorkspace',
  });

  const watchedForm = useMemo<TemplateFormData>(
    () =>
      normalizeTemplateFormForSnapshot({
        jobTitle: watchedJobTitle ?? null,
        company: watchedCompany ?? null,
        location: watchedLocation ?? null,
        showProfileImage: watchedShowProfileImage,
        showUp2promoBranding: watchedShowUp2promoBranding,
        accentColor: watchedAccentColor ?? null,
        layout: watchedLayout,
        theme: watchedTheme,
        headerStyle: watchedHeaderStyle,
        headerImage: watchedHeaderImage ?? null,
        buttonShape: watchedButtonShape ?? null,
        language: watchedLanguage,
        visibility: watchedVisibility,
        leadFormEnabled: watchedLeadFormEnabled,
        leadFormVisibilityMode: watchedLeadFormVisibilityMode,
        leadFormAutoOpenDelay: watchedLeadFormAutoOpenDelay,
        leadsAutoShareWithWorkspace: watchedLeadsAutoShareWithWorkspace,
      }),
    [
      watchedCompany,
      watchedJobTitle,
      watchedLanguage,
      watchedLeadFormAutoOpenDelay,
      watchedLeadFormEnabled,
      watchedLeadFormVisibilityMode,
      watchedLeadsAutoShareWithWorkspace,
      watchedLocation,
      watchedShowProfileImage,
      watchedShowUp2promoBranding,
      watchedAccentColor,
      watchedLayout,
      watchedTheme,
      watchedHeaderStyle,
      watchedHeaderImage,
      watchedButtonShape,
      watchedVisibility,
    ],
  );

  const canEditTemplate =
    canManage && publishStatus !== ProfileTemplatePublishStatus.SYNCING;

  const isDraftTemplate =
    publishStatus === ProfileTemplatePublishStatus.DRAFT && canManage;

  const {
    isLeavePromptOpen,
    confirmLeave,
    runWithoutGuard,
    setLeavePromptOpen,
  } = useNavigationLeaveGuard(isDraftTemplate && isEditorReady);

  const editorRef = useRef({
    workspaceId: '',
    templateId: '',
    canManage: false,
    canEditTemplate: false,
    name: '',
    description: '',
    policies: {} as ProfileTemplateFieldPoliciesDto,
    modules: [] as ProfileModuleDto[],
    teamIds: [] as string[],
    form: normalizeTemplateFormForSnapshot(
      contentToFormValues({ modules: [] }),
    ),
    fixedLeadFormFields,
    saveFailedLabel: pt.errors.saveFailed,
    canUseProfileBranding,
  });

  useEffect(() => {
    editorRef.current = {
      workspaceId: workspace?.id ?? '',
      templateId: templateId ?? '',
      canManage,
      canEditTemplate,
      name,
      description,
      policies,
      modules,
      teamIds: templateTeamIds,
      form: watchedForm,
      fixedLeadFormFields,
      saveFailedLabel: pt.errors.saveFailed,
      canUseProfileBranding,
    };
  }, [
    canEditTemplate,
    canUseProfileBranding,
    canManage,
    description,
    fixedLeadFormFields,
    modules,
    name,
    policies,
    pt.errors.saveFailed,
    templateId,
    templateTeamIds,
    watchedForm,
    workspace?.id,
  ]);

  const isValueDisabled = () => !canEditTemplate;

  const flushSave = useCallback(async () => {
    const state = editorRef.current;
    if (
      !state.workspaceId ||
      !state.templateId ||
      !state.canManage ||
      !state.canEditTemplate
    ) {
      return;
    }
    if (!state.name.trim()) return;

    const prepared = prepareTemplateDetailSave({
      state: {
        workspaceId: state.workspaceId,
        templateId: state.templateId,
        canManage: state.canManage,
        canEditTemplate: state.canEditTemplate,
        name: state.name,
        description: state.description,
        policies: state.policies,
        form: state.form,
        modules: state.modules,
        teamIds: state.teamIds,
        fixedLeadFormFields: state.fixedLeadFormFields,
        saveFailedLabel: state.saveFailedLabel,
        canUseProfileBranding: state.canUseProfileBranding,
      },
      pendingFormPatch: pendingFormPatchRef.current,
      previousSnapshot: previousSnapshotRef.current,
    });
    pendingFormPatchRef.current = prepared.clearedPendingPatch;
    if (prepared.kind === 'noop') return;

    setSaveError(null);
    markSaving();

    const result = await executeTemplateDetailSave(prepared);
    if (result.kind === 'error') {
      markIdle();
      setSaveError(result.message);
      toast.error(result.message);
      return;
    }

    if (result.publishStatus) {
      setPublishStatus(result.publishStatus);
    }
    // Do not reset the editor from the save response — parallel autosave keystrokes
    // would be wiped. The snapshot we just sent is the source of truth for the form.
    previousSnapshotRef.current = prepared.snapshot;
    markSaved();
  }, [markIdle, markSaved, markSaving]);

  useEffect(() => {
    if (!isEditorReady) return;
    for (const fieldName of TEMPLATE_FORM_FIELD_NAMES) {
      register(fieldName);
    }
  }, [isEditorReady, register]);

  const queueSave = useCallback(
    (formPatch?: Partial<TemplateFormData>) => {
      if (!isEditorReady || !canEditTemplate) return;
      if (formPatch) {
        pendingFormPatchRef.current = {
          ...pendingFormPatchRef.current,
          ...formPatch,
        };
      }
      if (saveTimeoutRef.current) clearTimeout(saveTimeoutRef.current);
      saveTimeoutRef.current = setTimeout(() => {
        void flushSave();
      }, 400);
    },
    [canEditTemplate, flushSave, isEditorReady],
  );

  const handlePolicyChange = (
    key: ProfileTemplateFieldKey,
    mode: ProfileTemplateFieldMode,
  ) => {
    if (!canEditTemplate) return;
    setPolicies((prev) => ({ ...prev, [key]: mode }));
    queueSave();
  };

  const applyTemplateToForm = useCallback(
    (t: ProfileTemplate) => {
      const parsedContent = parseTemplateContent(t.content);
      const formValues = contentToFormValues(parsedContent);
      const parsedModules = (parsedContent.modules ??
        []) as unknown as ProfileModuleDto[];

      setName(t.name);
      setSavedName(t.name);
      setDescription(t.description ?? '');
      setTemplateTeamIds(t.teamIds ?? []);
      setPublishStatus(
        t.publishStatus ?? ProfileTemplatePublishStatus.PUBLISHED,
      );
      setLiveRevision(
        t.revision > 1 ||
          t.publishStatus === ProfileTemplatePublishStatus.PUBLISHED ||
          t.publishStatus === ProfileTemplatePublishStatus.SYNCING
          ? t.revision
          : null,
      );
      setPolicies(
        normalizeFieldPolicies(
          parseFieldPolicies((t as { fieldPolicies?: unknown }).fieldPolicies),
        ),
      );
      const modulesWithIds = ensureProfileModuleEntryIds(parsedModules);
      const sanitizedLoadedModules =
        sanitizeModulesForTemplateApi(modulesWithIds);
      reset(formValues);
      setModules(modulesWithIds);

      previousSnapshotRef.current = buildTemplateEditorSnapshot({
        name: t.name,
        description: t.description ?? '',
        policies: normalizeFieldPolicies(
          parseFieldPolicies((t as { fieldPolicies?: unknown }).fieldPolicies),
        ),
        form: normalizeTemplateFormForSnapshot(formValues),
        modules: sanitizedLoadedModules,
        teamIds: t.teamIds ?? [],
      });
    },
    [reset],
  );

  useEffect(() => {
    applyTemplateToFormRef.current = applyTemplateToForm;
  }, [applyTemplateToForm]);

  const load = useCallback(async () => {
    if (!workspace?.id || !templateId) return;
    setLoading(true);
    setIsEditorReady(false);
    autosaveArmedRef.current = false;
    pendingFormPatchRef.current = {};
    if (saveTimeoutRef.current) {
      clearTimeout(saveTimeoutRef.current);
      saveTimeoutRef.current = null;
    }
    try {
      const res = await ProfileTemplatesApi.findOne({
        path: { workspaceId: workspace.id, templateId },
      });
      const t = res.data as ProfileTemplate | undefined;
      if (!t) throw new Error('not found');
      lastSyncedTemplateIdRef.current = t.id;
      applyTemplateToForm(t);
      setIsEditorReady(true);
    } catch (err) {
      console.error(err);
      const isOnTemplateDetailRoute = pathnameRef.current.startsWith(
        `${shellPaths.moreProfileTemplates}/`,
      );
      if (!suppressNotFoundRef.current && isOnTemplateDetailRoute) {
        toast.error(pt.errors.notFound);
        runWithoutGuard(() => redirect(shellPaths.moreProfileTemplates));
      }
    } finally {
      setLoading(false);
    }
  }, [
    applyTemplateToForm,
    pt.errors.notFound,
    redirect,
    runWithoutGuard,
    templateId,
    workspace,
  ]);

  useEffect(() => {
    void (async () => {
      await load();
    })();
  }, [load]);

  useEffect(() => {
    return () => {
      if (saveTimeoutRef.current) clearTimeout(saveTimeoutRef.current);
    };
  }, [templateId, workspace?.id]);

  useEffect(() => {
    if (canEditTemplate) return;
    if (saveTimeoutRef.current) {
      clearTimeout(saveTimeoutRef.current);
      saveTimeoutRef.current = null;
    }
    pendingFormPatchRef.current = {};
  }, [canEditTemplate]);

  useEffect(() => {
    const mode = watchedLeadFormVisibilityMode;
    const delay = watchedLeadFormAutoOpenDelay;
    if (mode !== 'auto_open' && delay != null && delay !== 0) {
      setValue('leadFormAutoOpenDelay', 0);
    }
  }, [setValue, watchedLeadFormAutoOpenDelay, watchedLeadFormVisibilityMode]);

  const previewProfileData = useMemo(() => {
    const locationsById = new Map(
      locations.map((location) => [location.id, location]),
    );
    return buildTemplateDetailPreviewProfileData({
      previewSampleName: pt.previewSampleName,
      form: applyTemplateBrandingEntitlement(
        watchedForm,
        canUseProfileBranding,
      ),
      modules,
      fixedLeadFormFields,
      workspace: workspace ?? null,
      locationsById,
    });
  }, [
    canUseProfileBranding,
    fixedLeadFormFields,
    locations,
    modules,
    pt.previewSampleName,
    watchedForm,
    workspace,
  ]);

  useEffect(() => {
    if (!isEditorReady) return;

    if (!autosaveArmedRef.current) {
      const prepared = prepareTemplateDetailSave({
        state: {
          workspaceId: editorRef.current.workspaceId,
          templateId: editorRef.current.templateId,
          canManage: editorRef.current.canManage,
          canEditTemplate: editorRef.current.canEditTemplate,
          name: editorRef.current.name,
          description: editorRef.current.description,
          policies: editorRef.current.policies,
          form: editorRef.current.form,
          modules: editorRef.current.modules,
          teamIds: editorRef.current.teamIds,
          fixedLeadFormFields: editorRef.current.fixedLeadFormFields,
          saveFailedLabel: editorRef.current.saveFailedLabel,
          canUseProfileBranding: editorRef.current.canUseProfileBranding,
        },
        pendingFormPatch: pendingFormPatchRef.current,
        previousSnapshot: previousSnapshotRef.current,
      });
      pendingFormPatchRef.current = prepared.clearedPendingPatch;
      // Absorb hydrate/normalize drift into the baseline — do not PATCH on enter.
      if (prepared.kind === 'ready') {
        previousSnapshotRef.current = prepared.snapshot;
      }
      autosaveArmedRef.current = true;
      return;
    }

    queueSave(watchedForm);
  }, [isEditorReady, queueSave, watchedForm]);

  useEffect(() => {
    if (!isEditorReady || !autosaveArmedRef.current) return;
    queueSave();
  }, [
    description,
    isEditorReady,
    modules,
    name,
    policies,
    queueSave,
    templateTeamIds,
  ]);

  useEffect(() => {
    if (
      publishStatus !== ProfileTemplatePublishStatus.SYNCING ||
      !workspace?.id ||
      !templateId
    ) {
      return;
    }

    const interval = window.setInterval(() => {
      void ProfileTemplatesApi.findOne({
        path: { workspaceId: workspace.id, templateId },
      })
        .then((res) => {
          const next = res.data?.publishStatus;
          if (next) setPublishStatus(next);
        })
        .catch(() => undefined);
    }, 3000);

    return () => window.clearInterval(interval);
  }, [publishStatus, templateId, workspace?.id]);

  const handleTeamIdsChange = useCallback(
    (next: string[]) => {
      setTemplateTeamIds(next);
      queueSave();
    },
    [queueSave],
  );

  const { handlePublish, handleDiscardChanges, handleBack, handleDelete } =
    useTemplateDetailLifecycle({
      workspaceId: workspace?.id,
      templateId,
      canManage,
      pt,
      applyTemplateToForm,
      goBack,
      runWithoutGuard,
      setPublishing,
      setDiscardingChanges,
      setPublishStatus,
      setLiveRevision,
      setRevisionHistoryKey,
      setDeleteConfirmOpen,
      saveTimeoutRef,
      pendingFormPatchRef,
      suppressNotFoundRef,
    });

  const publishStatusLabel = useMemo(() => {
    switch (publishStatus) {
      case ProfileTemplatePublishStatus.PUBLISHED:
        return pt.publishStatus.published;
      case ProfileTemplatePublishStatus.SYNCING:
        return pt.publishStatus.syncing;
      default:
        return pt.publishStatus.draft;
    }
  }, [publishStatus, pt.publishStatus]);

  const sharedMemberValueProps = useMemo(
    () => ({
      workspaceId: workspace?.id,
      templateId,
      publishStatus,
      memberRoster,
      memberRosterLoading,
      hasAssignedTeams: templateTeamIds.length > 0,
      memberValuesDict,
    }),
    [
      memberRoster,
      memberRosterLoading,
      memberValuesDict,
      publishStatus,
      templateId,
      templateTeamIds.length,
      workspace?.id,
    ],
  );

  const handleModulesChange = useCallback(
    (next: ProfileModuleDto[]) => {
      const withIds = ensureProfileModuleEntryIds(next);
      setModules(withIds);
      queueSave();
    },
    [queueSave],
  );

  const handleModuleEntryPolicyChange = useCallback(
    (moduleId: string, entryId: string, mode: ProfileTemplateFieldMode) => {
      setModules((prev) =>
        setEntryPolicyOnModule(prev, moduleId, entryId, mode),
      );
      queueSave();
    },
    [queueSave],
  );

  const overviewDict = useMemo(
    (): ProfileTemplateDetailOverviewDict => ({
      generalTitle: pt.detailOverview.generalTitle,
      generalDescription: pt.detailOverview.generalDescription,
      nameLabel: dict.workspace.teams.form.name,
      namePlaceholder: pt.detailOverview.namePlaceholder,
      nameRequired: pt.detailOverview.nameRequired,
      descriptionLabel: dict.workspace.teams.form.description,
      descriptionPlaceholder: pt.detailOverview.descriptionPlaceholder,
      publish: pt.publish,
      publishing: pt.publishing,
      discardChanges: pt.discardChanges,
      discardingChanges: pt.discardingChanges,
      publishStatus: pt.publishStatus,
      detailTeams: {
        ...pt.detailTeams,
        selectionCount: pt.detailTeams.selectionCount,
        noSelection: pt.detailTeams.noSelection,
        members: pt.detailTeams.members,
      },
      liveRevisionLabel: pt.detailOverview.liveRevisionLabel,
      viewRevisionHistory: pt.detailOverview.viewRevisionHistory,
    }),
    [
      dict.workspace.teams.form.description,
      dict.workspace.teams.form.name,
      pt.detailOverview.descriptionPlaceholder,
      pt.detailOverview.generalDescription,
      pt.detailOverview.generalTitle,
      pt.detailOverview.liveRevisionLabel,
      pt.detailOverview.namePlaceholder,
      pt.detailOverview.nameRequired,
      pt.detailOverview.viewRevisionHistory,
      pt.detailTeams,
      pt.discardChanges,
      pt.discardingChanges,
      pt.publish,
      pt.publishStatus,
      pt.publishing,
    ],
  );

  const revisionHistoryDict = useMemo(
    () => pt.revisionHistory,
    [pt.revisionHistory],
  );

  const handleViewRevisionHistory = useCallback(() => {
    setActiveTab('history');
  }, []);

  const handleRevisionRestored = useCallback(
    (template: ProfileTemplate) => {
      applyTemplateToForm(template);
    },
    [applyTemplateToForm],
  );

  const handleNameChange = useCallback(
    (value: string) => {
      setName(value);
      if (value.trim()) setSavedName(value);
      queueSave();
    },
    [queueSave],
  );

  const handleDescriptionChange = useCallback(
    (value: string) => {
      setDescription(value);
      queueSave();
    },
    [queueSave],
  );

  const handleFieldChange = useCallback(
    <K extends keyof TemplateFormData>(key: K, value: TemplateFormData[K]) => {
      setValue(key, value as never, { shouldDirty: true });
      queueSave({ [key]: value } as Partial<TemplateFormData>);
    },
    [queueSave, setValue],
  );

  return {
    dict,
    pt,
    pe,
    workspace,
    templateId,
    loading,
    canManage,
    canEditTemplate,
    workspaceHasTeam,
    autosaveStatus,
    saveError,
    name,
    savedName,
    description,
    policies,
    modules,
    templateTeamIds,
    publishStatus,
    publishing,
    discardingChanges,
    liveRevision,
    revisionHistoryKey,
    activeTab,
    setActiveTab,
    showPreview,
    setShowPreview,
    deleteConfirmOpen,
    setDeleteConfirmOpen,
    isLeavePromptOpen,
    setLeavePromptOpen,
    confirmLeave,
    errors,
    setValue,
    queueSave,
    handlePolicyChange,
    policyLabels,
    watchedForm,
    watchedJobTitle,
    watchedCompany,
    watchedLocation,
    watchedLanguage,
    watchedVisibility,
    isValueDisabled,
    sharedMemberValueProps,
    handleModulesChange,
    handleModuleEntryPolicyChange,
    handleBack,
    handleDelete,
    overviewDict,
    revisionHistoryDict,
    handleViewRevisionHistory,
    handleRevisionRestored,
    handleNameChange,
    handleDescriptionChange,
    handleFieldChange,
    handleTeamIdsChange,
    handlePublish,
    handleDiscardChanges,
    publishStatusLabel,
    previewProfileData,
  };
}

export type UseTemplateDetailEditorReturn = ReturnType<
  typeof useTemplateDetailEditor
>;
